import DashboardView from 'dashboard/DashboardView.react';
import Field from 'components/Field/Field.react';
import Fieldset from 'components/Fieldset/Fieldset.react';
import FlowView from 'components/FlowView/FlowView.react';
import FormButton from 'components/FormButton/FormButton.react';
import FormModal from 'components/FormModal/FormModal.react';
import KeyField from 'components/KeyField/KeyField.react';
import Label from 'components/Label/Label.react';
import Modal from 'components/Modal/Modal.react';
import React from 'react';
import renderFlowFooterChanges from 'lib/renderFlowFooterChanges';
import styles from 'dashboard/Settings/Settings.scss';
import TextInput from 'components/TextInput/TextInput.react';
import Toggle from 'components/Toggle/Toggle.react';
import Toolbar from 'components/Toolbar/Toolbar.react';
import unique from 'lib/unique';
import { Promise } from 'parse';

export default class SecuritySettings extends DashboardView {
  constructor() {
    super();
    this.section = 'App Settings';
    this.subsection = 'Security & Keys';

    this.state = {
      saveState: null,
      dataFetched: false,
      showResetDialog: false,
      resetError: false,
      passwordInput: '',
    };
  }

  renderForm({
    fields, changes, setField, resetFields,
  }) {
    const currentApp = this.context.currentApp;

    const resetDialog = (<FormModal
      title="Reset Master Key"
      subtitle="Be careful, this action cannot be undone!"
      open={this.state.showResetDialog}
      type={Modal.Types.DANGER}
      submitText="Reset"
      inProgressText={'Resetting\u2026'}
      enabled={this.state.passwordInput.length > 0}
      onSubmit={() => this.context.currentApp.resetMasterKey(this.state.passwordInput)}
      onClose={() => this.setState({ showResetDialog: false })}
      clearFields={() => { this.setState({ passwordInput: '' }); }}
      buttonsInCenter={false}>
      <Field
        labelWidth={60}
        label={<Label text="Your password" description={'We want to make sure it\u2019s really you.'} />}
        input={
          <TextInput
            hidden
            value={this.state.passwordInput}
            onChange={passwordInput => this.setState({ passwordInput })}
            placeholder="Password" />
        } />
    </FormModal>);

    const permissions = this.props.initialFields ?
      (<Fieldset legend="App Permissions" description="Helpful in development, but turn this off when you launch.">
        <Field
          className="oneliner"
          label={<Label text="Allow client class creation" className="oneliner" description={'Allows new classes to be created without the master key. Once your app\u2019s classes are finalized, you should disable access to protect your app from malicious users.'} />}
          input={
            <Toggle
              value={fields.client_class_creation_enabled}
              onChange={setField.bind(this, 'client_class_creation_enabled')} />
          } />
      </Fieldset>) : null;

    return (
      <div className={[styles.settings_page, 'ShowFooter'].join(' ')}>
        <Toolbar section={this.section} subsection={this.subsection} />
        <div className={styles.holder}>
          <Fieldset legend="Application URLs">
            <div className={styles.settingsBox}>
              <Field
                label={<Label text="Public URL" />}
                input={<KeyField>{currentApp.serverURL.replace('/1/', '/')}</KeyField>} />

              <Field
                label={<Label text="API URL Address" />}
                input={<KeyField>{currentApp.serverURL}</KeyField>} />

              <Field
                label={<Label text="LiveQuery URL Address" />}
                input={<KeyField>{currentApp.serverURL.replace(/(http)(s)?\:\/\//, 'ws$2://')}</KeyField>} />

              {
                currentApp.graphQLServerURL && (
                  <Field
                    label={<Label text="GraphQL API URL" />}
                    input={<KeyField>{currentApp.graphQLServerURL}</KeyField>} />
                )
              }

              <Field
                label={<Label text="Files URL" />}
                input={<KeyField>{currentApp.params.s3_base_url || `https://${currentApp.serviceName}.files-sashido.cloud/`}</KeyField>} />

              {/*
                <Field
                  label={<Label text="Resumable Big Files Upload URL" />}
                  input={<KeyField>https://upload.files-sashido.cloud/2/files/</KeyField>} />
              */}
            </div>
          </Fieldset>
          <Fieldset legend="App Keys" description="These are the unique identifiers used to access this app.">
            <div className={styles.settingsBox}>
              <Field
                label={<Label text="Application ID" description={<span>Main ID that uniquely specifies this app. <br />Used with one of the keys below.</span>} />}
                input={<KeyField>{currentApp.applicationId}</KeyField>} />

              <Field
                label={<Label text="Client Key" description={<span>Use this in consumer clients, such as <br />the iOS or Android SDKs.</span>} />}
                input={<KeyField>{currentApp.clientKey}</KeyField>} />

              <Field
                label={<Label text="JavaScript Key" description="Use this when making requests from JavaScript clients." />}
                input={<KeyField>{currentApp.javascriptKey}</KeyField>} />

              <Field
                label={<Label text=".NET Key" description={<span>Use this when making requests from <br />Windows, Xamarin, or Unity clients.</span>} />}
                input={<KeyField>{currentApp.dotNetKey}</KeyField>} />

              <Field
                label={<Label text="REST API Key" description="Use this when making requests from server-side REST applications. Keep it secret!" />}
                input={<KeyField name="REST">{currentApp.restKey}</KeyField>} />

              <Field
                label={<Label text="Webhook Key" description="Use this when implementing a Cloud Code Webhook. Keep it secret!" />}
                input={<KeyField name="Webhook">{currentApp.webhookKey}</KeyField>} />

              <Field
                label={<Label text="File Key" description="Use this key when migrating to your own Parse Server to ensure your new server has access to existing files." />}
                input={<KeyField name="File">{currentApp.fileKey}</KeyField>} />

              <Field
                label={<Label text="Master Key" description="Using this key overrides all permissions. Not usable on client SDKs. Keep it secret!" />}
                input={<KeyField name="Master Key" hidden>{currentApp.masterKey}</KeyField>} />

              <Field
                label={<Label text="Database URL" description="..." />}
                input={<KeyField name="Database URL" hidden>{currentApp.databaseUri.replace('&slaveOk=true&', '&')}</KeyField>} />
            </div>
          </Fieldset>

          {permissions}

          <Fieldset legend="Reset Master Key" description="Use this when your key has been compromised.">
            <Field
              label={<Label text="Reset master key" description={<span>This will permanently reset the master <br />key to a newly generated key.</span>} />}
              input={<FormButton color="red" value="Reset Master Key" onClick={() => this.setState({ showResetDialog: true })} />} />
          </Fieldset>
          {resetDialog}
        </div>
      </div>
    );
  }

  renderContent() {
    if (!this.props.initialFields) {
      return <Toolbar section={this.section} subsection={this.subsection} />;
    }

    const initialFields = this.props.initialFields;

    return (
      <FlowView
        initialFields={initialFields}
        footerContents={({ changes }) => renderFlowFooterChanges(changes, initialFields, userFieldOptions)}
        onSubmit={({ changes, fields, setField }) => {
          return this.props.saveChanges(changes).then(({ successes, failures }) => {
            for (const k in successes) {
              setField(k, successes[k]);
            }

            if (Object.keys(failures).length > 0) {
              return { error: Object.values(failures).join(' ') };
            }
          })
            .catch(({ error, failures = {} }) => {
              return { error: unique(Object.values(failures).concat(error)).join(' ') };
            });
        }}
        validate={() => ''}
        renderForm={this.renderForm.bind(this)} />
    );
  }
}


const userFieldOptions = {
  client_class_creation_enabled: {
    friendlyName: 'class creation on clients',
    type: 'boolean',
  },
};
